import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { SiVisa, SiMastercard, SiPaypal, SiApplepay, SiGooglepay } from "react-icons/si";
import { AuroraBackground } from "@/components/AuroraBackground";
import { QuoteWidget } from "@/components/QuoteWidget";
import { HeroShowcase } from "@/components/HeroShowcase";
import { ProcessedFilesFeed } from "@/components/ProcessedFilesFeed";
import { BrandStrip } from "@/components/BrandStrip";
import { TuningSelector } from "@/components/TuningSelector";
import { PricingCompare } from "@/components/PricingCompare";
import { ThemePicker } from "@/components/ThemePicker";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { InstallAppButton } from "@/components/InstallAppButton";
import { InstallBanner } from "@/components/InstallBanner";
import { BrandMark } from "@/components/BrandMark";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import {
  Cpu, Gauge, Wind, ShieldOff, FileWarning, KeyRound, Zap, ArrowRight,
  UploadCloud, ScanLine, Download, CheckCircle2, Star, Stethoscope,
  ShieldAlert, BatteryCharging, Cog, Database, Activity, Wrench, Menu, X,
} from "lucide-react";

const services = [
  { tk: "stage1", id: "stage1", icon: Gauge, code: "STAGE 1", credits: 8 },
  { tk: "fap", id: "fap_off", icon: Wind, code: "FAP OFF", credits: 6 },
  { tk: "egr", id: "egr_off", icon: Wind, code: "EGR OFF", credits: 5 },
  { tk: "adblue", id: "adblue_off", icon: ShieldOff, code: "ADBLUE", credits: 7 },
  { tk: "immo", id: "immo_off", icon: KeyRound, code: "IMMO OFF", credits: 6 },
  { tk: "dtc", id: "dtc_off", icon: FileWarning, code: "DTC", credits: 4 },
  { tk: "tcu", id: "tcu_stage1", icon: Cog, code: "BOÎTE TCU", credits: 9 },
  { tk: "airbag", id: "airbag_crash", icon: ShieldAlert, code: "AIRBAG", credits: 7 },
  { tk: "srs", id: "airbag_eeprom", icon: ShieldAlert, code: "SRS EEPROM", credits: 8 },
  { tk: "hybrid", id: "hybrid_reset", icon: BatteryCharging, code: "HYBRIDE", credits: 7 },
];

const features = [
  { icon: Wrench, tk: "f1", to: "#services" },
  { icon: Database, tk: "f2", to: "#pricing" },
  { icon: Activity, tk: "f3", to: "#process" },
];

const steps = [
  { icon: UploadCloud, n: "01", tk: "s1" },
  { icon: ScanLine, n: "02", tk: "s2" },
  { icon: Cpu, n: "03", tk: "s3" },
  { icon: Download, n: "04", tk: "s4" },
];

const navLinks = [
  { tk: "services", href: "#services" },
  { tk: "specs", href: "#specs" },
  { tk: "process", href: "#process" },
  { tk: "quote", href: "#quote" },
  { tk: "obd", to: "/obd" },
  { tk: "pricing", href: "#pricing" },
];

const payments = [SiVisa, SiMastercard, SiPaypal, SiApplepay, SiGooglepay];

export const Landing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const [reviews, setReviews] = useState({ reviews: [], average: 0, count: 0 });
  const [menuOpen, setMenuOpen] = useState(false);
  const [svcCredits, setSvcCredits] = useState({});
  const [svcFrom, setSvcFrom] = useState({});
  useEffect(() => { api.get("/public/reviews").then(({ data }) => setReviews(data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/catalog").then(({ data }) => {
      const m = {}; const f = {};
      (data.services || []).forEach((s) => { m[s.id] = s.credits; f[s.id] = !!s.price_from; });
      setSvcCredits(m); setSvcFrom(f);
    }).catch(() => {});
  }, []);
  useEffect(() => { document.body.style.overflow = menuOpen ? "hidden" : ""; return () => { document.body.style.overflow = ""; }; }, [menuOpen]);
  const go = () => navigate(user ? (user.role === "admin" ? "/admin" : "/client") : "/login");
  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="relative min-h-screen text-white overflow-hidden">
      <AuroraBackground />
      <InstallBanner />
      <div className="relative z-10">
        {/* Nav */}
        <header className="sticky top-0 z-50 glass flex items-center justify-between px-6 md:px-12 h-20 relative">
          <div className="m-stripe-bar absolute top-0 left-0" />
          <BrandMark size="md" />
          <div className="flex items-center gap-3 md:gap-4">
            <a href="#quote" className="hidden sm:inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-aurora-cyan hover:text-white transition-colors">
              <ScanLine className="w-3.5 h-3.5" /> {t("common.freeQuote")}
            </a>
            <LanguageSwitcher />
            <ThemePicker />
            <InstallAppButton />
            <button onClick={go} data-testid="header-login-btn" className="hidden sm:block px-5 py-2.5 bg-aurora-cyan text-black font-display font-bold uppercase tracking-wider text-sm rounded-sm">
              {user ? t("common.myspace") : t("common.login")}
            </button>
            <button onClick={() => setMenuOpen(true)} data-testid="menu-open-btn" aria-label="Menu" className="w-10 h-10 grid place-items-center border border-white/15 rounded-sm hover:border-aurora-cyan/60 hover:bg-white/5 transition-colors">
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </header>

        {/* Slide-in menu */}
        <AnimatePresence>
          {menuOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                onClick={closeMenu} data-testid="menu-overlay"
                className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm"
              />
              <motion.aside
                initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
                transition={{ type: "tween", duration: 0.35, ease: [0.22, 0.7, 0.3, 1] }}
                data-testid="menu-panel"
                className="fixed top-0 right-0 z-[61] h-full w-full sm:w-[400px] glass border-l border-white/10 flex flex-col overflow-hidden"
              >
                <div className="m-stripe-bar absolute top-0 left-0" />
                <div className="flex items-center justify-between px-6 h-20 border-b border-white/5">
                  <BrandMark size="md" />
                  <button onClick={closeMenu} data-testid="menu-close-btn" aria-label="Fermer le menu" className="w-10 h-10 grid place-items-center border border-white/15 rounded-sm hover:border-aurora-cyan/60 transition-colors">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <nav className="flex-1 px-6 py-6 flex flex-col overflow-y-auto">
                  {navLinks.map((l, i) => {
                    const cls = "group flex items-center justify-between py-4 border-b border-white/5 font-display text-2xl uppercase tracking-tight hover:text-aurora-cyan transition-colors";
                    const arrow = <ArrowRight className="w-5 h-5 text-zinc-600 group-hover:text-aurora-cyan group-hover:translate-x-1 transition-all" />;
                    return (
                      <motion.div key={l.tk} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 + i * 0.05 }}>
                        {l.to ? (
                          <Link to={l.to} onClick={closeMenu} data-testid={`menu-link-${i}`} className={cls}>{t(`nav.${l.tk}`)} {arrow}</Link>
                        ) : (
                          <a href={l.href} onClick={closeMenu} data-testid={`menu-link-${i}`} className={cls}>{t(`nav.${l.tk}`)} {arrow}</a>
                        )}
                      </motion.div>
                    );
                  })}
                </nav>
                <div className="px-6 pb-8 pt-4 border-t border-white/5 space-y-4">
                  {reviews.count > 0 && (
                    <div className="flex items-center gap-2" data-testid="menu-trust-badge">
                      <div className="flex">{[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`w-4 h-4 ${i <= Math.round(reviews.average) ? "text-signal-processing fill-signal-processing" : "text-zinc-600"}`} />)}</div>
                      <span className="font-mono text-[11px] text-zinc-500 uppercase tracking-wider">{reviews.average}/5 · {reviews.count} {t("reviews.reviewsWord")}</span>
                    </div>
                  )}
                  <button onClick={() => { closeMenu(); go(); }} data-testid="menu-login-btn" className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-aurora-cyan text-black font-display font-bold uppercase tracking-wider rounded-sm">
                    {user ? t("common.myspace") : t("common.login")} <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </motion.aside>
            </>
          )}
        </AnimatePresence>

        {/* Hero */}
        <section className="relative px-6 md:px-12 pt-14 pb-20">
          <div className="grid lg:grid-cols-12 gap-10 items-center max-w-7xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="lg:col-span-6">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 glass rounded-full mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-signal-success animate-pulse-glow" />
                <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">{t("hero.badge")}</span>
              </div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold uppercase leading-[0.92] tracking-tight">
                {t("hero.titleLine1")}<br /><span className="text-aurora-cyan text-glow-cyan">{t("hero.titleLine2")}</span><br />{t("hero.titleLine3")}
              </h1>
              <p className="mt-6 text-zinc-400 text-base max-w-md leading-relaxed">
                {t("hero.subtitle")}
              </p>
              <ul className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[11px] uppercase tracking-wider text-zinc-400">
                <li className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-signal-success" /> {t("hero.badge1")}</li>
                <li className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-signal-success" /> {t("hero.badge2")}</li>
                <li className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-signal-success" /> {t("hero.badge3")}</li>
              </ul>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <button onClick={go} data-testid="hero-cta-btn" className="group flex items-center gap-2 px-6 py-3.5 bg-aurora-cyan text-black font-display font-bold uppercase tracking-wider rounded-sm">
                  {t("hero.cta")} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
                <a href="#quote" className="px-6 py-3.5 border border-white/20 font-display font-bold uppercase tracking-wider rounded-sm hover:border-aurora-cyan/60 hover:bg-white/5 transition-colors">
                  {t("common.freeQuote")}
                </a>
              </div>
              <p className="mt-4 font-mono text-[11px] text-zinc-500">{t("hero.note")}</p>
            </motion.div>

            <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.15 }} className="lg:col-span-6 relative">
              <HeroShowcase />
            </motion.div>
          </div>
        </section>

        {/* Feature cards */}
        <section className="px-6 md:px-12 pb-4">
          <div className="grid md:grid-cols-3 gap-5 max-w-7xl mx-auto">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.a
                  key={f.tk} href={f.to} data-testid={`feature-card-${i}`}
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="group glass rounded-md p-7 hover:border-aurora-cyan/40 transition-all"
                >
                  <div className="w-12 h-12 grid place-items-center border border-aurora-cyan/30 rounded-md bg-aurora-cyan/10 mb-5">
                    <Icon className="w-6 h-6 text-aurora-cyan" />
                  </div>
                  <h3 className="font-display text-xl font-bold uppercase tracking-tight">{t(`features.${f.tk}t`)}</h3>
                  <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{t(`features.${f.tk}d`)}</p>
                  <span className="mt-4 inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-widest text-aurora-cyan">
                    {t("common.learnMore")} <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </motion.a>
              );
            })}
          </div>
        </section>

        {/* Services */}
        <section id="services" className="px-6 md:px-12 py-20 border-t border-white/5">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
              <div>
                <div className="font-mono text-xs text-aurora-cyan tracking-[0.3em] uppercase mb-3">{t("servicesSec.kicker")}</div>
                <h2 className="font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight max-w-xl">{t("servicesSec.title")}</h2>
              </div>
              <p className="text-zinc-400 max-w-sm text-sm">{t("servicesSec.subtitle")}</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {services.map((s, i) => {
                const Icon = s.icon;
                return (
                  <motion.div
                    key={s.code}
                    initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.04 }}
                    className="group glass rounded-md p-6 hover:border-aurora-cyan/40 hover:-translate-y-1 transition-all"
                  >
                    <div className="flex items-center justify-between mb-5">
                      <div className="w-11 h-11 grid place-items-center border border-white/10 rounded-sm bg-white/5 group-hover:border-aurora-cyan/40 group-hover:bg-aurora-cyan/10 transition-colors">
                        <Icon className="w-5 h-5 text-aurora-cyan" />
                      </div>
                      <span className="font-mono text-[10px] tracking-widest text-zinc-500 uppercase">{s.code}</span>
                    </div>
                    <h3 className="font-display text-xl font-semibold uppercase tracking-tight">{t(`svc.${s.tk}.name`)}</h3>
                    <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{t(`svc.${s.tk}.desc`)}</p>
                    <div className="mt-5 pt-4 border-t border-white/5 flex items-center justify-between">
                      <span className="font-mono text-xs text-zinc-500">{(s.id && svcFrom[s.id]) ? t("common.from") : t("common.price")}</span>
                      <span className="font-mono text-sm text-aurora-cyan font-bold">{(s.id && svcCredits[s.id]) || s.credits} {t("common.credits")}</span>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Trust feed */}
        <ProcessedFilesFeed />

        {/* Tuning spec selector */}
        <TuningSelector />

        {/* Brand logos */}
        <BrandStrip />

        {/* Process */}
        <section id="process" className="px-6 md:px-12 py-20 border-t border-white/5">
          <div className="max-w-7xl mx-auto">
            <div className="font-mono text-xs text-aurora-cyan tracking-[0.3em] uppercase mb-3">{t("processSec.kicker")}</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight mb-12">{t("processSec.title")}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {steps.map((s) => {
                const Icon = s.icon;
                return (
                  <div key={s.n} className="glass rounded-md p-6 relative">
                    <div className="font-display text-5xl font-bold text-aurora-cyan/10 absolute top-4 right-5">{s.n}</div>
                    <Icon className="w-7 h-7 text-aurora-cyan mb-4" />
                    <h3 className="font-display text-lg font-semibold uppercase tracking-tight">{t(`steps.${s.tk}t`)}</h3>
                    <p className="mt-2 text-sm text-zinc-400">{t(`steps.${s.tk}d`)}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Devis instantané public */}
        <QuoteWidget />

        {/* OBD decoder CTA */}
        <section className="px-6 md:px-12 py-16 border-t border-white/5">
          <div className="max-w-7xl mx-auto glass rounded-md p-8 md:p-10 flex items-center justify-between flex-wrap gap-6">
            <div className="flex items-center gap-5">
              <div className="w-14 h-14 grid place-items-center border border-aurora-cyan/40 rounded-md bg-aurora-cyan/10">
                <Stethoscope className="w-7 h-7 text-aurora-cyan" />
              </div>
              <div>
                <h3 className="font-display text-2xl font-bold uppercase tracking-tight">{t("obdCta.title")}</h3>
                <p className="text-zinc-400 text-sm mt-1">{t("obdCta.desc")}</p>
              </div>
            </div>
            <Link to="/obd" data-testid="obd-landing-cta" className="flex items-center gap-2 px-6 py-3.5 border border-aurora-cyan/40 text-aurora-cyan font-display font-bold uppercase tracking-wider rounded-sm hover:bg-aurora-cyan/10 transition-colors">
              {t("common.openTool")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        {/* Testimonials */}
        {reviews.reviews.length > 0 && (
          <section id="reviews" className="px-6 md:px-12 py-20 border-t border-white/5">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
                <div>
                  <div className="font-mono text-xs text-aurora-cyan tracking-[0.3em] uppercase mb-3">{t("reviews.kicker")}</div>
                  <h2 className="font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight">{t("reviews.title")}</h2>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex">{[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`w-5 h-5 ${i <= Math.round(reviews.average) ? "text-signal-processing fill-signal-processing" : "text-zinc-700"}`} />)}</div>
                  <div><span className="font-display text-2xl font-bold">{reviews.average}</span><span className="font-mono text-xs text-zinc-500">/5 · {reviews.count} {t("reviews.reviewsWord")}</span></div>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {reviews.reviews.map((r) => (
                  <div key={r.id} className="glass rounded-md p-6">
                    <div className="flex mb-3">{[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`w-4 h-4 ${i <= r.rating ? "text-signal-processing fill-signal-processing" : "text-zinc-700"}`} />)}</div>
                    {r.comment && <p className="text-sm text-zinc-300 leading-relaxed mb-4">"{r.comment}"</p>}
                    <div className="flex items-center justify-between pt-3 border-t border-white/5">
                      <span className="font-display uppercase tracking-tight text-sm">{r.name}</span>
                      <span className="font-mono text-[10px] text-zinc-500">{r.vehicle}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Pricing comparison */}
        <div className="max-w-7xl mx-auto">
          <PricingCompare onCta={go} />
        </div>

        <footer className="px-6 md:px-12 pt-16 pb-10 border-t border-white/5">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-4 gap-10 mb-12">
              <div>
              <div className="mb-4"><BrandMark size="md" /></div>
                <p className="text-sm text-zinc-400 max-w-xs">{t("footer.tagline")}</p>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-aurora-cyan mb-4">{t("footer.services")}</div>
                <ul className="space-y-2 text-sm text-zinc-400">
                  <li><a href="#services" className="hover:text-aurora-cyan transition-colors">Stage 1 / 2</a></li>
                  <li><a href="#services" className="hover:text-aurora-cyan transition-colors">FAP · EGR · AdBlue</a></li>
                  <li><a href="#services" className="hover:text-aurora-cyan transition-colors">DTC · IMMO OFF</a></li>
                </ul>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-aurora-cyan mb-4">{t("footer.tools")}</div>
                <ul className="space-y-2 text-sm text-zinc-400">
                  <li><Link to="/obd" className="hover:text-aurora-cyan transition-colors">{t("footer.obd")}</Link></li>
                  <li><a href="#quote" className="hover:text-aurora-cyan transition-colors">{t("footer.instantQuote")}</a></li>
                  <li><a href="#pricing" className="hover:text-aurora-cyan transition-colors">{t("footer.creditPacks")}</a></li>
                </ul>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-aurora-cyan mb-4">{t("footer.account")}</div>
                <ul className="space-y-2 text-sm text-zinc-400">
                  <li><button onClick={go} className="hover:text-aurora-cyan transition-colors">{t("common.login")}</button></li>
                  <li><a href="#reviews" className="hover:text-aurora-cyan transition-colors">{t("footer.reviews")}</a></li>
                </ul>
              </div>
            </div>
            <div className="flex items-center gap-5 mb-8 flex-wrap">
              <span className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">{t("common.securePayment")}</span>
              {payments.map((Icon, i) => <Icon key={i} className="w-9 h-9 text-zinc-400" />)}
            </div>
            <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="font-mono text-[10px] text-zinc-600 tracking-widest uppercase">{t("footer.copyright")}</div>
              <div className="font-mono text-[10px] text-zinc-600 tracking-widest uppercase">{t("footer.disclaimer")}</div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};
